import { TextField as TextFieldMui } from "@mui/material";
import {
  StepValue,
  TableElement,
  TextFieldElement as TextFieldElementProps,
} from "@types";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import RawEditor, { RawEditorHandle } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";

export interface TextFieldEditorHandle {
  getMarkdownContent: () => string;
}

type Props = {
  element: TextFieldElementProps | TableElement;
  value: string;
  disabled?: boolean;
  onChange: (id: string, value: StepValue) => void;
  textFieldReadOnly?: boolean;
};

export const TextField = forwardRef<TextFieldEditorHandle, Props>(({
  disabled,
  onChange,
  element,
  value,
  textFieldReadOnly = false,
}, ref) => {
  const editorRef = useRef<RawEditorHandle>(null);
  const normalizedValue = value ?? "";
  const lastInternalValueRef = useRef<string>(normalizedValue);

  useImperativeHandle(ref, () => ({
    getMarkdownContent: () => {
      if (element.type === "textField" && element.options.type === "number") {
        return normalizedValue;
      }
      return editorRef.current?.getMarkdownContent() ?? normalizedValue;
    },
  }), [element, normalizedValue]);

  // Reload editor content only when external value changes
  // (not when value changes due to internal edits via autosave)
  useEffect(() => {
    // Skip if the value matches what we last saved internally
    if (normalizedValue === lastInternalValueRef.current) {
      return;
    }
    // External value change detected, reload content
    editorRef.current?.reloadContent(normalizedValue);
    lastInternalValueRef.current = normalizedValue;
  }, [normalizedValue]);

  const handleChange = (newValue: string) => {
    lastInternalValueRef.current = newValue;
    onChange(element.id, { answer: newValue });
  };

  return (
    <div key={element.id} className="flex flex-col">
      {element.type === "textField" && element.options.type === "number" ? (
        <TextFieldMui
          inputProps={{ "data-testid": element.id }}
          hiddenLabel
          type={element.options.type}
          key={element.id}
          id={element.id}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange(element.id, { answer: event.target.value })
          }
          multiline={!!element.options.rows}
          fullWidth
          value={normalizedValue}
          minRows={element.options.rows}
          disabled={disabled}
          InputProps={{
            readOnly: textFieldReadOnly,
          }}
        />
      ) : (
        <RawEditor
          ref={editorRef}
          data-testid={element.id}
          key={element.id}
          onChange={handleChange}
          markdown={normalizedValue}
        />
      )}
    </div>
  );
});
TextField.displayName = "TextField";
export default TextField;
